import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, User as UserIcon, Phone, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const LEAD_SOURCES = ["Google", "Facebook", "Instagram", "YouTube", "Website", "Walk-in", "Reference", "Seminar", "Webinar"];
const RASHIS = ["Mesha", "Vrishabha", "Mithuna", "Karka", "Simha", "Kanya", "Tula", "Vrishchika", "Dhanu", "Makara", "Kumbha", "Meena"];

export default function Clients() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", mobile: "", email: "", gender: "", dob: "", place_of_birth: "", rashi: "", nakshatra: "", mahadasha: "", occupation: "", lead_source: "Walk-in", notes: "" });

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (source && source !== "all") params.lead_source = source;
    try {
      const { data } = await api.get("/clients", { params });
      setItems(data);
    } catch (err) { console.error("clients load failed:", err?.message); }
  }, [q, source]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.full_name.trim()) { toast.error("Name required"); return; }
    try {
      const { data } = await api.post("/clients", form);
      toast.success(`Client ${data.client_code} created`);
      setOpen(false);
      setForm({ full_name: "", mobile: "", email: "", gender: "", dob: "", place_of_birth: "", rashi: "", nakshatra: "", mahadasha: "", occupation: "", lead_source: "Walk-in", notes: "" });
      load();
    } catch (e) { toast.error("Create failed"); }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Directory</div>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight text-slate-900 dark:text-white mt-1">Clients</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-pill bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 h-11 px-5" data-testid="new-client-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-client-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl">Create client profile</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <Field label="Full name *"><Input value={form.full_name} onChange={e=>setForm({...form, full_name:e.target.value})} data-testid="client-name" /></Field>
              <Field label="Mobile"><Input value={form.mobile} onChange={e=>setForm({...form, mobile:e.target.value})} data-testid="client-mobile" /></Field>
              <Field label="Email"><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} /></Field>
              <Field label="Gender">
                <Select value={form.gender} onValueChange={v=>setForm({...form, gender:v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Date of birth"><Input type="date" value={form.dob} onChange={e=>setForm({...form, dob:e.target.value})} /></Field>
              <Field label="Place of birth"><Input value={form.place_of_birth} onChange={e=>setForm({...form, place_of_birth:e.target.value})} /></Field>
              <Field label="Rashi">
                <Select value={form.rashi} onValueChange={v=>setForm({...form, rashi:v})}>
                  <SelectTrigger><SelectValue placeholder="Select rashi" /></SelectTrigger>
                  <SelectContent>{RASHIS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Nakshatra"><Input value={form.nakshatra} onChange={e=>setForm({...form, nakshatra:e.target.value})} /></Field>
              <Field label="Mahadasha"><Input value={form.mahadasha} onChange={e=>setForm({...form, mahadasha:e.target.value})} placeholder="e.g. Saturn Mahadasha" /></Field>
              <Field label="Occupation"><Input value={form.occupation} onChange={e=>setForm({...form, occupation:e.target.value})} /></Field>
              <Field label="Lead source">
                <Select value={form.lead_source} onValueChange={v=>setForm({...form, lead_source:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAD_SOURCES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Notes</Label>
                <Textarea rows={3} value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} />
              </div>
            </div>
            <div className="flex justify-end mt-4 gap-2">
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-[#1E3A8A] btn-pill" data-testid="submit-new-client">Create client</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by name, phone, rashi, mahadasha, occupation…" value={q} onChange={e=>setQ(e.target.value)} className="pl-9 h-11 rounded-xl" data-testid="clients-search" />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-full md:w-48 h-11 rounded-xl" data-testid="clients-source-filter"><SelectValue placeholder="All sources" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {LEAD_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 rounded-2xl text-center border-dashed" data-testid="clients-empty">
          <UserIcon className="w-10 h-10 mx-auto text-slate-400" strokeWidth={1.25} />
          <p className="mt-3 text-slate-500">No clients yet. Create your first devotee's profile.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(c => (
            <Card key={c.id} onClick={() => nav(`/clients/${c.id}`)} className="p-5 rounded-2xl cursor-pointer card-lift border-slate-200 dark:border-slate-800" data-testid={`client-card-${c.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-500/20 dark:to-amber-700/20 border border-amber-300/50 flex items-center justify-center text-amber-700 dark:text-amber-300 font-semibold">
                  {c.full_name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-medium text-slate-900 dark:text-white truncate">{c.full_name}</div>
                  <div className="font-mono text-[10px] text-amber-600 dark:text-amber-400">{c.client_code}</div>
                </div>
                {c.is_repeat && <Badge variant="secondary" className="text-[10px]">Repeat</Badge>}
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
                {c.mobile && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {c.mobile}</div>}
                {c.rashi && <div className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-amber-500" /> {c.rashi} • {c.nakshatra || "—"}</div>}
                {c.mahadasha && <div className="text-xs text-slate-500">Mahadasha: {c.mahadasha}</div>}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
                <span className="text-slate-500">{c.lead_source || "—"}</span>
                <span className="font-mono text-slate-500">{c.total_visits} visits</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
